/**
 * WellnessPage — today first, always.
 *
 * The day's state is the top of the screen and needs no navigation to reach. Everything
 * else (history, adherence, meals, workouts) is below it. The single floating Log button
 * is reachable from every tab, so nothing is ever more than two taps from being recorded.
 *
 * TONE RULES, enforced in review (PRD section 9):
 *   - This page RECORDS. It never advises. It shows a value against a target the user set
 *     and says nothing about what to do next.
 *   - Nothing scolds. An unlogged item says "not yet" in the same voice as everything else.
 *     No red, no X, no streak-breaking language, no "behind".
 *   - A null macro renders as an em dash, never 0.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type {
  WellnessAdherence,
  WellnessDailyStatus,
  WellnessMedication,
  WellnessMetricType,
  WellnessSession,
  WellnessSessionTypeDef,
  WellnessTarget,
  WellnessTrends,
} from '../../shared/types';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../home/ui';
import LogSheet from './LogSheet';
import WeekTab from './WeekTab';
import TrendsCharts from './TrendsChart';
import MealLibrary from './MealLibrary';
import PlanTab from './PlanTab';
import { TargetMeter, fmt, targetSide } from './ChartKit';
import {
  MACRO_KEYS,
  SESSION_LABELS,
  macro,
  macroFormatted,
  macroLabel,
  macroUnit,
  shortDay,
  targetDirection,
  targetNumber,
  type MacroKey,
} from './format';

type Tab = 'today' | 'week' | 'plan' | 'trends' | 'meals' | 'training' | 'meds';

const TABS: { key: Tab; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'plan', label: 'Plan' },
  { key: 'trends', label: 'Trends' },
  { key: 'meals', label: 'Meals' },
  { key: 'training', label: 'Training' },
  { key: 'meds', label: 'Adherence' },
];

export default function WellnessPage() {
  const [tab, setTab] = useState<Tab>('today');
  const [sheet, setSheet] = useState(false);

  const [status, setStatus] = useState<WellnessDailyStatus | null>(null);
  const [medications, setMedications] = useState<WellnessMedication[]>([]);
  const [metricTypes, setMetricTypes] = useState<WellnessMetricType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [s, m, t] = await Promise.all([
        api.get<WellnessDailyStatus>('/wellness/today'),
        api.get<WellnessMedication[]>('/wellness/medications'),
        api.get<WellnessMetricType[]>('/wellness/metric-types'),
      ]);
      setStatus(s);
      setMedications(m);
      setMetricTypes(t);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 sm:px-6">
      <header className="pt-6 pb-4">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Wellness</h1>
        <p className="mt-1 text-sm text-ink-soft">
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </header>

      <nav className="-mx-4 mb-5 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                tab === t.key
                  ? 'bg-ink text-canvas'
                  : 'border border-border bg-surface text-ink-soft hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      {loading && (
        <Card className="p-5">
          <LoadingRows rows={5} />
        </Card>
      )}
      {!loading && error && (
        <Card className="p-5">
          <ErrorState what="your wellness data" onRetry={() => void load()} />
        </Card>
      )}

      {!loading && !error && (
        <>
          {tab === 'today' && <TodayTab status={status} onLog={() => setSheet(true)} />}
          {tab === 'week' && <WeekTab onChanged={() => void load()} />}
          {tab === 'plan' && <PlanTab onChanged={() => void load()} />}
          {tab === 'trends' && <TrendsTab />}
          {tab === 'meals' && <MealLibrary onChanged={() => void load()} />}
          {tab === 'training' && <TrainingTab />}
          {tab === 'meds' && <AdherenceTab />}
        </>
      )}

      <button
        type="button"
        onClick={() => setSheet(true)}
        className="fixed right-5 bottom-5 z-40 inline-flex min-h-14 items-center gap-2 rounded-full bg-sage px-6 font-medium text-white shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        <Icon name="check" className="h-5 w-5" />
        Log
      </button>

      <LogSheet
        open={sheet}
        onClose={() => setSheet(false)}
        onLogged={() => void load()}
        status={status}
        medications={medications}
        metricTypes={metricTypes}
      />
    </div>
  );
}

// ---------------------------------------------------------------- today

function TodayTab({
  status,
  onLog,
}: {
  status: WellnessDailyStatus | null;
  onLog: () => void;
}) {
  if (!status || !status.items.length) {
    return (
      <Card className="p-5">
        <Empty
          message="Nothing set up to track yet. Add a medication or log a reading to start."
          action={{ to: '/wellness', label: 'Log something' }}
        />
      </Card>
    );
  }

  const targetFor = (slug: string) => status.targets.find((t) => t.slug === slug);

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <CardTitle icon="check">Today</CardTitle>
        <ul className="mt-4 space-y-2">
          {status.items.map((i) => {
            const done = i.done >= i.expected;
            return (
              <li
                key={i.key}
                className="flex items-center gap-3 rounded-xl border border-border bg-canvas px-3 py-2.5"
              >
                <span
                  aria-hidden
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                    done ? 'bg-sage text-white' : 'border border-border bg-surface'
                  }`}
                >
                  {done && <Icon name="check" className="h-3.5 w-3.5" />}
                </span>
                <span className="min-w-0 flex-1 text-ink">{i.label}</span>
                {/* Factual, never a judgement. "Not yet" is the whole vocabulary. */}
                <span className="shrink-0 text-sm text-ink-soft">
                  {i.expected > 1 ? `${i.done} of ${i.expected}` : done ? 'Logged' : 'Not yet'}
                </span>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          onClick={onLog}
          className="mt-4 min-h-11 w-full rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          Log something
        </button>
      </Card>

      <Card className="p-5">
        <CardTitle icon="pot">Food today</CardTitle>
        {status.nutrition.meals_logged === 0 ? (
          <p className="mt-4 text-sm text-ink-soft">No meals logged yet.</p>
        ) : (
          <>
            <p className="mt-1 text-xs text-ink-faint">
              Running totals from {status.nutrition.meals_logged}{' '}
              {status.nutrition.meals_logged === 1 ? 'meal' : 'meals'} so far today.
            </p>
            <ul className="mt-4 space-y-3.5">
              {MACRO_KEYS.map((key) => {
                const target = targetFor(key);
                return (
                  <MacroMeterRow
                    key={key}
                    macroKey={key}
                    value={status.nutrition[key]}
                    target={target ? targetNumber(target.value) : null}
                    targetRow={target}
                    incomplete={status.nutrition.partial.includes(key)}
                  />
                );
              })}
            </ul>
            {/* The honesty line. A SUM that skipped nulls would otherwise read as complete. */}
            {status.nutrition.partial.length > 0 && (
              <p className="mt-4 rounded-xl bg-honey-soft px-3 py-2 text-xs text-honey">
                Some meals had no {status.nutrition.partial.map(macroLabel).join(', ')} recorded, so
                those totals are only part of the day.
              </p>
            )}
          </>
        )}
        {status.eating_window && (
          <p className="mt-3 text-sm text-ink-soft">
            Eating window {status.eating_window.start}–{status.eating_window.end} ·{' '}
            {status.eating_window.state === 'before'
              ? 'not open yet'
              : status.eating_window.state === 'open'
                ? 'open'
                : 'closed'}
          </p>
        )}
      </Card>

      <Card className="p-5">
        <CardTitle icon="broom">Training today</CardTitle>
        {status.sessions.length === 0 ? (
          <p className="mt-4 text-sm text-ink-soft">Nothing logged yet.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {status.sessions.map((s) => (
              <SessionRow key={s.id} session={s} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * One macro against the target set for it.
 *
 * A bare grid of six numbers was the complaint, and the numbers were the least of it: a
 * total means nothing without the target beside it, and 2,412 mg of sodium reads exactly
 * like 1,412 mg until something on screen says which side of 2,300 it fell on.
 *
 * Three states, and they must never be confused for each other:
 *   a number — a bar that fills toward the target, which is the end of the track
 *   over      — the overshoot drawn in clay, and captioned, because colour alone is not a label
 *   null      — an EM DASH and a DASHED EMPTY TRACK. Not a zero, not an empty solid bar.
 *               A zero reads as measured, and a measured zero is how a gap goes unnoticed.
 *
 * Nothing here praises a target met or notes one missed: at 2pm a running total is a third
 * of the day, so any verdict would be about the hour rather than the food. The single
 * exception is a ceiling already crossed, which is true no matter what time it is.
 */
function MacroMeterRow({
  macroKey,
  value,
  target,
  targetRow,
  incomplete,
}: {
  macroKey: MacroKey;
  value: number | null;
  target: number | null;
  /** The target row, so direction comes from D1 rather than from the slug. */
  targetRow?: WellnessTarget;
  incomplete: boolean;
}) {
  const unit = macroUnit(macroKey);
  // Direction from the target row (migration 0028). The slug fallback inside
  // targetDirection only covers a row written before the column existed.
  const direction = targetDirection(macroKey, targetRow);
  const glyph = direction === 'up' ? '≥' : '≤';
  const crossed = direction === 'down' && targetSide(value, target, direction) === 'past';

  return (
    <li>
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-baseline gap-1.5">
          <span className="text-sm font-medium text-ink">{macroLabel(macroKey)}</span>
          {incomplete && value !== null && (
            <span className="rounded-full bg-honey-soft px-1.5 py-px text-[10px] font-medium text-honey">
              incomplete
            </span>
          )}
        </span>
        <span className="shrink-0 text-sm text-ink-soft">
          {value === null ? (
            <span className="text-ink-faint">{macro(null)} not recorded</span>
          ) : (
            <>
              <b
                className="text-base font-semibold text-ink"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {macroFormatted(value)}
              </b>{' '}
              {unit}
              {target !== null && (
                <span className="text-ink-faint">
                  {' '}
                  of {glyph} {fmt(target)}
                </span>
              )}
            </>
          )}
        </span>
      </div>

      <div className="mt-1.5">
        <TargetMeter
          value={value}
          target={target}
          direction={direction}
          incomplete={incomplete}
        />
      </div>

      {crossed && value !== null && target !== null && (
        <p className="mt-1 text-xs font-medium text-clay">
          {fmt(value - target)} {unit} over the {fmt(target)} {unit} target
        </p>
      )}
      {target === null && value !== null && (
        <p className="mt-1 text-xs text-ink-faint">No target set for this one.</p>
      )}
    </li>
  );
}

function SessionRow({
  session,
  def,
}: {
  session: WellnessSession;
  def?: WellnessSessionTypeDef;
}) {
  const bits = [
    session.duration_min ? `${session.duration_min} min` : null,
    session.avg_hr ? `avg ${session.avg_hr}` : null,
    session.max_hr ? `max ${session.max_hr}` : null,
  ].filter(Boolean);

  return (
    <li className="rounded-xl border border-border bg-canvas px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-ink">
          {def?.label ?? SESSION_LABELS[session.session_type] ?? session.session_type}
          {!session.completed && (
            <span className="ml-2 text-xs font-normal text-honey">planned</span>
          )}
        </span>
        <span className="shrink-0 text-sm text-ink-soft">{bits.join(' · ')}</span>
      </div>
      {/* What to do, wherever the session is shown. "It just shows the date" was the
          complaint, and a date is not a workout. */}
      {def?.prescription && <p className="mt-1 text-sm text-ink-soft">{def.prescription}</p>}
      {def?.zone_note && <p className="mt-0.5 text-xs text-ink-faint">{def.zone_note}</p>}
      {session.notes && <p className="mt-1 text-sm text-ink-soft">{session.notes}</p>}
    </li>
  );
}

// ---------------------------------------------------------------- generic loader

function useList<T>(path: string) {
  const [data, setData] = useState<T[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      setData(await api.get<T[]>(path));
    } catch {
      setError(true);
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, reload: load };
}

// ---------------------------------------------------------------- trends

const RANGES = [14, 30, 90] as const;

/**
 * The range picker sits ABOVE every card rather than inside one, because it scopes all of
 * them. A refetch holds the previous render at reduced opacity instead of dropping back to
 * a skeleton — swapping 30 days for 90 should not blank the screen and jump the layout.
 */
function TrendsTab() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<WellnessTrends | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setBusy(true);
    try {
      setData(await api.get<WellnessTrends>(`/wellness/trends?days=${days}`));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-soft">Last {days} days</p>
        <div className="flex gap-1" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              aria-pressed={days === r}
              aria-label={`Last ${r} days`}
              className={`min-h-11 rounded-full px-3.5 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                days === r ? 'bg-ink text-canvas' : 'border border-border bg-surface text-ink-soft'
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {error && (
        <Card className="p-5">
          <ErrorState what="your trends" onRetry={() => void load()} />
        </Card>
      )}
      {!error && !data && (
        <Card className="p-5">
          <LoadingRows rows={5} />
        </Card>
      )}
      {!error && data && (
        <div className={busy ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
          <TrendsCharts data={data} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- adherence

/**
 * The doctor-visit view. `unlogged` is shown as its own number beside taken and skipped,
 * never folded into either — the difference between "I didn't take it" and "nobody wrote
 * it down" is the entire subject of this module.
 */
function AdherenceTab() {
  const { data, error, reload } = useList<WellnessAdherence>('/wellness/medications/adherence?days=30');

  if (error)
    return (
      <Card className="p-5">
        <ErrorState what="adherence" onRetry={() => void reload()} />
      </Card>
    );
  if (!data)
    return (
      <Card className="p-5">
        <LoadingRows rows={4} />
      </Card>
    );
  if (!data.length)
    return (
      <Card className="p-5">
        <Empty
          message="No medications set up yet."
          action={{ to: '/wellness', label: 'Log something' }}
        />
      </Card>
    );

  return (
    <div className="space-y-4">
      {data.map((a) => (
        <Card key={a.medication_id} className="p-5">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-semibold text-ink">
              {a.name}
              {a.dose && <span className="ml-2 text-sm font-normal text-ink-soft">{a.dose}</span>}
            </h2>
            <span className="text-xs text-ink-soft capitalize">{a.category}</span>
          </div>

          <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-sage-soft px-2 py-2">
              <dt className="text-xs text-sage">Taken</dt>
              <dd className="text-xl font-semibold text-sage">{a.taken}</dd>
            </div>
            <div className="rounded-xl bg-canvas px-2 py-2">
              <dt className="text-xs text-ink-soft">Skipped</dt>
              <dd className="text-xl font-semibold text-ink">{a.skipped}</dd>
            </div>
            <div className="rounded-xl bg-canvas px-2 py-2">
              <dt className="text-xs text-ink-soft">Unlogged</dt>
              <dd className="text-xl font-semibold text-ink">{a.unlogged}</dd>
            </div>
          </dl>

          <p className="mt-2 text-xs text-ink-soft">
            Over {a.days} days. Unlogged is counted separately — it means no record, not a
            missed dose.
          </p>

          <div className="mt-3 flex flex-wrap gap-1" aria-hidden>
            {a.history.map((h) => (
              <span
                key={h.date}
                title={`${h.date}: ${h.status ?? 'unlogged'}`}
                className={`h-4 w-4 rounded-sm ${
                  h.status === 'taken'
                    ? 'bg-sage'
                    : h.status === 'skipped'
                      ? 'bg-clay-soft'
                      : 'border border-border bg-canvas'
                }`}
              />
            ))}
          </div>
          {/* The grid is decorative; the numbers above carry the same information in text. */}
          <p className="mt-2 text-xs text-ink-soft">
            Each square is a day, oldest first. Empty means unlogged.
          </p>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- training

function TrainingTab() {
  const { data, error, reload } = useList<WellnessSession>('/wellness/sessions');
  const { data: types } = useList<WellnessSessionTypeDef>('/wellness/session-types');

  if (error)
    return (
      <Card className="p-5">
        <ErrorState what="your workouts" onRetry={() => void reload()} />
      </Card>
    );
  if (!data)
    return (
      <Card className="p-5">
        <LoadingRows rows={4} />
      </Card>
    );
  if (!data.length)
    return (
      <Card className="p-5">
        <Empty
          message="No workouts logged yet."
          action={{ to: '/wellness', label: 'Log a workout' }}
        />
      </Card>
    );

  const byDate = new Map<string, WellnessSession[]>();
  for (const s of data) {
    if (!byDate.has(s.session_date)) byDate.set(s.session_date, []);
    byDate.get(s.session_date)!.push(s);
  }

  return (
    <div className="space-y-4">
      {[...byDate.entries()].map(([date, sessions]) => (
        <Card key={date} className="p-5">
          <CardTitle icon="calendar">{shortDay(date)}</CardTitle>
          <ul className="mt-3 space-y-2">
            {sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                def={(types ?? []).find((t) => t.slug === s.session_type)}
              />
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
